// src/services/broadcastPolling.ts
//
// Контроллер HTTP-polling — фолбэк к Pusher/Soketi WebSocket.
// Приводит polling-события к Pusher-форме и прокидывает их в существующий
// диспетчер handleWebSocketEvent, поэтому WS-хендлеры работают без изменений.
//
// См. 2026-05-29-broadcast-polling-frontend-prompt.md (разделы 5-7).

import { handleWebSocketEvent } from 'events/webSocketHandlers';
import { pollBroadcast } from 'services/broadcastApi';
import type { BroadcastPollResponse, PollingEvent, TaskState } from 'events/types';

// ─── Конфигурация темпа ────────────────────────────────────────────────
const ACTIVE_INTERVAL_MS = 700; // нормальный темп polling — чаще опрашиваем, чтобы стрим выглядел плавнее
const HIDDEN_INTERVAL_MS = 30000; // throttle для свёрнутой вкладки
const OFFLINE_RETRY_MS = 5000; // ровный ретрай при offline (без шума в Sentry)
const BACKOFF_503_MS = [1000, 2000, 5000]; // backoff при 503 → дальше ровно 5с
const DEDUPE_CAP = 500; // сколько обработанных event id держим в памяти

// ─── Адаптеры формата ──────────────────────────────────────────────────
// Pusher отдаёт плоский payload (status/time/...поля data на верхнем уровне),
// polling — вложенный. Приводим polling к Pusher-форме перед диспетчеризацией.

type PusherShape = { event: string; data: Record<string, unknown> };

export const pollingEventToPusherShape = (e: PollingEvent): PusherShape => ({
  event: e.event,
  data: {
    status: e.status,
    time: e.time,
    ...e.data,
  },
});

export const taskStateToPusherShape = (state: TaskState): PusherShape => ({
  event: state.event,
  data: {
    status: state.status,
    time: state.time,
    ...state.data,
  },
});

// ─── Хранилище cursor'а ────────────────────────────────────────────────
// Курсор переживает перезагрузку вкладки: при повторном открытии первый polling
// отдаёт пропущенные события журнала + актуальный снапшот живых задач.

const cursorKey = (userId: string): string => `broadcast:cursor:${userId}`;

export const cursorStorage = {
  get(userId: string): string | null {
    try {
      return localStorage.getItem(cursorKey(userId));
    } catch {
      return null;
    }
  },
  set(userId: string, cursor: string | null): void {
    try {
      // null = «журнал пуст»: убираем ключ, следующий запрос пойдёт без cursor.
      if (cursor === null) {
        localStorage.removeItem(cursorKey(userId));
      } else {
        localStorage.setItem(cursorKey(userId), cursor);
      }
    } catch {
      /* ignore quota / private mode */
    }
  },
  clear(userId: string): void {
    try {
      localStorage.removeItem(cursorKey(userId));
    } catch {
      /* ignore */
    }
  },
};

// ─── Дедупликация по event id ──────────────────────────────────────────
// Нужна только в момент переключения WS↔polling: replay может повторно
// прислать уже показанное по WS событие. Окно последних N id, FIFO.

export interface Dedupe {
  has(id: string): boolean;
  add(id: string): void;
}

export const createDedupe = (cap: number = DEDUPE_CAP): Dedupe => {
  const seen = new Set<string>();
  const order: string[] = [];
  return {
    has: (id) => seen.has(id),
    add: (id) => {
      if (seen.has(id)) return;
      seen.add(id);
      order.push(id);
      if (order.length > cap) {
        const evicted = order.shift();
        if (evicted !== undefined) seen.delete(evicted);
      }
    },
  };
};

// ─── Диспетчеризация ответа ────────────────────────────────────────────
type Dispatch = (eventName: string, data: Record<string, unknown>) => void;

/**
 * Прокидывает один ответ /broadcast/poll в WS-диспетчер.
 * events[] — журнал (по порядку, с дедупом по id), task_states — снапшот
 * (всегда переписывает локальное состояние, без дедупа).
 *
 * Завершение задачи приходит терминальным событием в events[] и финализирует
 * UI через существующие хендлеры — исчезновение task_id из снапшота не требует
 * синтетического события.
 */
export const dispatchPollResponse = (
  resp: BroadcastPollResponse,
  dedupe: Dedupe,
  dispatch: Dispatch = handleWebSocketEvent
): void => {
  for (const ev of resp.events ?? []) {
    if (dedupe.has(ev.id)) continue;
    dedupe.add(ev.id);
    const shaped = pollingEventToPusherShape(ev);
    dispatch(shaped.event, shaped.data);
  }

  const taskStates = resp.task_states ?? {};
  for (const taskId of Object.keys(taskStates)) {
    const shaped = taskStateToPusherShape(taskStates[taskId]);
    dispatch(shaped.event, shaped.data);
  }
};

// ─── Поллер ────────────────────────────────────────────────────────────
export interface BroadcastPoller {
  start(): void;
  stop(): void;
  isRunning(): boolean;
}

interface PollerOptions {
  userId: string;
  // Инъекции для тестов:
  dispatch?: Dispatch;
  poll?: (cursor: string | null) => Promise<BroadcastPollResponse>;
}

interface ApiErrorLike {
  status?: number;
}

const statusOf = (err: unknown): number | undefined =>
  err && typeof err === 'object' && 'status' in err ? (err as ApiErrorLike).status : undefined;

export const createBroadcastPoller = (options: PollerOptions): BroadcastPoller => {
  const { userId, dispatch = handleWebSocketEvent, poll = pollBroadcast } = options;
  const dedupe = createDedupe();

  let running = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let backoffIdx = 0;

  const baseInterval = (): number =>
    typeof document !== 'undefined' && document.hidden ? HIDDEN_INTERVAL_MS : ACTIVE_INTERVAL_MS;

  const nextBackoff = (): number => {
    const ms = BACKOFF_503_MS[Math.min(backoffIdx, BACKOFF_503_MS.length - 1)];
    backoffIdx += 1;
    return ms;
  };

  const pollOnce = async (): Promise<void> => {
    const cursor = cursorStorage.get(userId);
    const resp = await poll(cursor);
    dispatchPollResponse(resp, dedupe, dispatch);
    // Сохраняем cursor только после успешной диспетчеризации — иначе пропуск
    // событий при сбое посреди обработки.
    cursorStorage.set(userId, resp.cursor);
  };

  const schedule = (delay: number): void => {
    if (!running) return;
    timer = setTimeout(() => void tick(), delay);
  };

  const tick = async (): Promise<void> => {
    if (!running) return;
    let delay = baseInterval();
    try {
      await pollOnce();
      backoffIdx = 0;
    } catch (err) {
      const status = statusOf(err);
      if (status === 422) {
        // Баг фронта (cursor невалиден) — сбросить и сообщить.
        cursorStorage.clear(userId);
        backoffIdx = 0;
        // eslint-disable-next-line no-console
        console.error('[broadcast] invalid cursor, reset to null', err);
      } else if (status === 503) {
        delay = nextBackoff(); // Redis недоступен — backoff, cursor НЕ трогаем.
      } else if (status === undefined) {
        // Сетевая ошибка / offline — ровный ретрай, без шума.
        delay = OFFLINE_RETRY_MS;
      } else {
        // 401 уже обработан apiReq (редирект). Прочее — лёгкий backoff + лог.
        // eslint-disable-next-line no-console
        console.warn('[broadcast] poll failed', status, err);
        delay = nextBackoff();
      }
    }
    schedule(delay);
  };

  const onVisibilityChange = (): void => {
    // Вкладка снова активна — опрашиваем сразу, не дожидаясь HIDDEN-интервала.
    if (running && typeof document !== 'undefined' && !document.hidden) {
      if (timer) clearTimeout(timer);
      void tick();
    }
  };

  const onOnline = (): void => {
    if (running) {
      if (timer) clearTimeout(timer);
      void tick();
    }
  };

  return {
    start(): void {
      if (running) return; // идемпотентно
      running = true;
      backoffIdx = 0;
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange);
      }
      if (typeof window !== 'undefined') {
        window.addEventListener('online', onOnline);
      }
      void tick(); // первый опрос немедленно
    },
    stop(): void {
      if (!running) return;
      running = false;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
      }
    },
    isRunning: () => running,
  };
};
